import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { PublicHeader, PublicFooter } from "@/components/PublicShell";
import {
  ArrowRight, Repeat, ShieldCheck, Info, Heart, Lock, FileText,
  ClockCounterClockwise, HandHeart, Sparkle,
} from "@phosphor-icons/react";

// Backs onto a reserved, unrestricted "General Fund" campaign — not tied to
// any specific cause — that the backend auto-creates on first request
// (see CampaignController::GENERAL_FUND_SLUG). Donors here never see or
// pick a campaign; the slug only exists to reuse the proven donate/
// checkout, payment, receipt and Meta-pixel pipeline unchanged.
const GENERAL_FUND_SLUG = "general-fund";

const FALLBACK_PRESETS = [
  { amount: 100, label: "Feeds a rescued animal for a week" },
  { amount: 300, label: "Covers basic medicine for a month" },
  { amount: 500, label: "Funds a vet check-up" },
  { amount: 1000, label: "Supports a full treatment course" },
];

const FAQS = [
  {
    q: "Can I cancel my monthly donation anytime?",
    a: "Yes. There's no lock-in or contract — you can cancel your recurring donation at any time, no questions asked.",
  },
  {
    q: "Do I get a tax receipt?",
    a: "Yes. Every donation is eligible for an 80G tax deduction. You can opt in and add your PAN during checkout to receive a compliant receipt by email.",
  },
  {
    q: "Where does my monthly donation go?",
    a: "Monthly gifts go into our General Fund, which we use flexibly wherever the need is greatest — emergency rescues, medical treatment, food and shelter — rather than being locked to a single campaign.",
  },
  {
    q: "Is my payment secure?",
    a: "Yes. Payments are processed through Razorpay, a PCI-DSS compliant payment gateway used by leading Indian businesses. We never see or store your card details.",
  },
  {
    q: "How do I know SEVA is a genuine, registered organisation?",
    a: "We're happy to share our registration documents — Darpan registration, PAN, and 80G certificate — for anyone who wants to verify us before donating.",
  },
];

function roundUpTo(amount, base) {
  const n = Number(amount) || 0;
  const rem = n % base;
  if (rem === 0) return n;
  return n + (base - rem);
}

function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="seva-card !p-0 overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-3 font-medium"
      >
        <span>{q}</span>
        <span className={"transition-transform shrink-0 text-lg " + (open ? "rotate-45" : "")}>+</span>
      </button>
      {open && <div className="px-5 pb-5 text-[#56624F] leading-relaxed text-sm">{a}</div>}
    </div>
  );
}

export default function DonateMonthlyPage() {
  const nav = useNavigate();
  const [fund, setFund] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("500");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/campaigns/${GENERAL_FUND_SLUG}`)
      .then((r) => {
        setFund(r.data);
        const presets = r.data?.preset_amounts || [];
        setInput(String(presets[2]?.amount || presets[1]?.amount || 500));
      })
      .catch(() => setFund(null))
      .finally(() => setLoading(false));
  }, []);

  const presets = fund?.preset_amounts?.length ? fund.preset_amounts : FALLBACK_PRESETS;
  const minAmount = Number(fund?.min_amount || 100);
  const amount = Number(input) || 0;
  const belowMin = touched && amount > 0 && amount < minAmount;
  const empty = touched && !input.trim();
  const isCustom = !presets.find((p) => p.amount === amount);
  const roundSuggestion = useMemo(() => {
    if (!isCustom || amount < minAmount) return null;
    const up = roundUpTo(amount, 100);
    return up > amount ? up : null;
  }, [amount, isCustom, minAmount]);
  const canCheckout = amount >= minAmount;

  const pickPreset = (v) => { setInput(String(v)); setTouched(true); };

  const checkout = () => {
    if (!canCheckout) { setTouched(true); return; }
    setSubmitting(true);
    const params = new URLSearchParams();
    params.set("amount", String(amount));
    params.set("monthly", "1");
    nav(`/donate/${GENERAL_FUND_SLUG}?${params.toString()}`);
  };

  if (loading) return <div className="min-h-screen bg-[#FBF9F6]" />;

  return (
    <>
      <PublicHeader />

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 pt-10 md:pt-16 pb-6 text-center">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 bg-[#EEF4E9] px-3 py-1.5 rounded-full text-xs font-semibold text-[#2F7A4A] uppercase tracking-widest mb-5">
            <Repeat size={14} weight="fill" /> Monthly Giving
          </div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl leading-tight" data-testid="donate-monthly-title">
            One small monthly gift.<br className="hidden sm:block" /> A lifeline every single day.
          </h1>
          <p className="text-[#56624F] mt-4 max-w-xl mx-auto text-base md:text-lg">
            Emergencies don&rsquo;t wait for a campaign to fund them. As a monthly donor, you give us the
            certainty to act the moment an animal needs us &mdash; rescue, treatment, food and shelter, every month, no matter what.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 mt-6 text-xs sm:text-sm text-[#56624F]">
            <span className="inline-flex items-center gap-1.5"><Lock size={16} className="text-[#4FAC39]" /> Secure payments</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={16} className="text-[#4FAC39]" /> 80G tax benefits</span>
            <span className="inline-flex items-center gap-1.5"><ClockCounterClockwise size={16} className="text-[#4FAC39]" /> Cancel anytime</span>
          </div>
        </div>
      </section>

      {/* Amount picker */}
      <section className="max-w-2xl mx-auto px-4 sm:px-6 md:px-10 pb-4">
        <div className="seva-card space-y-5" id="amount-picker">
          <div>
            <div className="text-xs font-semibold text-[#56624F] uppercase tracking-wider mb-2">Choose your monthly gift</div>
            <div className="grid grid-cols-2 gap-2.5">
              {presets.map((p) => (
                <button
                  key={p.amount}
                  onClick={() => pickPreset(p.amount)}
                  data-testid={`monthly-amount-preset-${p.amount}`}
                  className={
                    "px-3 py-3 rounded-xl border text-left transition-all relative " +
                    (amount === p.amount
                      ? "border-[#4FAC39] bg-[#4FAC39] text-white shadow-md"
                      : "border-[#DCE5D4] bg-white text-[#1F2A1C] hover:border-[#4FAC39]/60")
                  }
                >
                  {p.amount === 500 && amount !== p.amount && (
                    <span className="absolute -top-2 right-2 bg-[#F5A623] text-white text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full">
                      Popular
                    </span>
                  )}
                  <div className="font-display font-bold text-lg">{formatINR(p.amount)}<span className="text-xs font-normal opacity-80">/mo</span></div>
                  <div className={"text-[11px] leading-tight mt-0.5 " + (amount === p.amount ? "text-white/90" : "text-[#56624F]")}>{p.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-[#56624F] uppercase tracking-wider whitespace-nowrap">Or custom &#8377;/mo</span>
              <input
                type="number" min={minAmount} value={input}
                onChange={(e) => { setInput(e.target.value); setTouched(true); }}
                onFocus={() => setTouched(true)}
                className={"flex-1 px-3 py-2 rounded-lg border outline-none bg-white text-sm " + (belowMin || empty ? "border-[#B84A34]" : "border-[#DCE5D4] focus:border-[#4FAC39]")}
                data-testid="monthly-amount-input"
              />
            </div>
            {(belowMin || empty) && (
              <div className="mt-1.5 text-xs text-[#B84A34] flex items-center gap-1" data-testid="monthly-min-amount-error">
                <Info size={12} weight="fill" /> Minimum {formatINR(minAmount)}
              </div>
            )}
            {roundSuggestion && (
              <button onClick={() => pickPreset(roundSuggestion)} className="mt-1.5 text-xs text-[#4FAC39] font-semibold hover:underline" data-testid="monthly-roundup-accept">
                Round up to {formatINR(roundSuggestion)}?
              </button>
            )}
          </div>

          <div className="bg-[#E6F3DC] border border-[#4FAC39]/30 rounded-lg px-3 py-2.5 text-xs text-[#2F7A4A] flex items-center gap-1.5">
            <ShieldCheck size={16} weight="fill" className="shrink-0" /> Cancel your monthly donation anytime. <strong>80G tax benefits</strong> apply.
          </div>

          <button
            onClick={checkout}
            disabled={!canCheckout || submitting}
            className="btn-primary w-full !py-4 text-sm tracking-wide uppercase disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            data-testid="donate-monthly-checkout-btn"
          >
            <Heart size={18} weight="fill" /> Become a Monthly Donor &mdash; {formatINR(amount)}/mo <ArrowRight size={16} />
          </button>
          <p className="text-xs text-center text-[#56624F] -mt-2">No hidden fees. Change or cancel your amount anytime.</p>
        </div>
      </section>

      {/* Why monthly */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-12 md:py-16">
        <h2 className="font-display font-bold text-2xl md:text-3xl text-center mb-8">Why monthly giving matters</h2>
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            {
              Icon: Sparkle,
              title: "We act the moment it counts",
              body: "A predictable monthly fund means we don't have to wait for a campaign to reach its goal before we can help an animal in front of us right now.",
            },
            {
              Icon: HandHeart,
              title: "Your rupee goes further",
              body: "Recurring gifts cut down on payment processing overhead and let us plan ahead — buying medicine and supplies in bulk, at lower cost per animal.",
            },
            {
              Icon: Repeat,
              title: "Effortless, flexible giving",
              body: "Set it once and we handle the rest. Pause, change your amount, or cancel anytime — completely in your control.",
            },
          ].map(({ Icon, title, body }, i) => (
            <div key={i} className="seva-card text-center">
              <div className="w-12 h-12 rounded-xl bg-[#EEF4E9] text-[#2F7A4A] grid place-items-center mx-auto mb-3">
                <Icon size={24} weight="fill" />
              </div>
              <h3 className="font-display font-bold text-base mb-1.5">{title}</h3>
              <p className="text-sm text-[#56624F] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust / verification */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 md:px-10 pb-12 md:pb-16">
        <div className="seva-card !bg-[#F5F8F2] flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white text-[#2F7A4A] grid place-items-center shrink-0">
              <ShieldCheck size={22} weight="fill" />
            </div>
            <div>
              <div className="font-semibold">Verified, registered NGO</div>
              <div className="text-xs text-[#56624F]">Darpan registration, PAN &amp; 80G certificate available on request.</div>
            </div>
          </div>
          <a href={`${process.env.PUBLIC_URL}/documents`} className="btn-secondary !py-2.5 !px-5 text-sm inline-flex items-center gap-2 shrink-0" data-testid="monthly-verify-link">
            <FileText size={16} /> View documents
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-2xl mx-auto px-4 sm:px-6 md:px-10 pb-24 md:pb-24">
        <h2 className="font-display font-bold text-2xl md:text-3xl text-center mb-6">Common questions</h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <FaqItem key={i} q={f.q} a={f.a} testid={`monthly-faq-${i}`} />
          ))}
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <div className="fixed bottom-0 inset-x-0 md:hidden bg-white border-t border-[#DCE5D4] z-30 p-3 flex items-center gap-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-[#56624F]">Your monthly gift</div>
          <div className="font-display font-bold text-sm truncate">{formatINR(amount)}/mo</div>
        </div>
        <a href="#amount-picker" className="btn-primary !py-3 !px-6 shrink-0" data-testid="mobile-sticky-donate">Donate</a>
      </div>

      <PublicFooter />
    </>
  );
}
